'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HealthCategory {
  id: string;
  label: string;
  description: string;
  icon: string;
  href: string;
}

interface HealthVerticalMenuProps {
  variant?: 'horizontal' | 'vertical';
  showDescriptions?: boolean;
}

const HealthVerticalMenu = ({ 
  variant = 'horizontal',
  showDescriptions = true 
}: HealthVerticalMenuProps) => {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  const healthCategories: HealthCategory[] = [
    {
      id: 'mental-health',
      label: 'Mental Health',
      description: 'Anxiety, depression, stress management',
      icon: 'SparklesIcon',
      href: '/health-vertical-pages?category=mental-health'
    },
    {
      id: 'chronic-conditions',
      label: 'Chronic Conditions',
      description: 'Diabetes, hypertension, heart health',
      icon: 'HeartIcon',
      href: '/health-vertical-pages?category=chronic-conditions'
    },
    {
      id: 'womens-health',
      label: "Women's Health",
      description: 'Reproductive health, pregnancy care',
      icon: 'UserIcon',
      href: '/health-vertical-pages?category=womens-health'
    },
    {
      id: 'pediatric-care',
      label: 'Pediatric Care',
      description: 'Child health, vaccinations, development',
      icon: 'AcademicCapIcon',
      href: '/health-vertical-pages?category=pediatric-care'
    },
    {
      id: 'dermatology',
      label: 'Dermatology',
      description: 'Skin conditions, acne, eczema',
      icon: 'SunIcon',
      href: '/health-vertical-pages?category=dermatology'
    },
    {
      id: 'nutrition',
      label: 'Nutrition & Wellness',
      description: 'Diet planning, weight management',
      icon: 'BeakerIcon',
      href: '/health-vertical-pages?category=nutrition'
    }
  ];

  if (variant === 'vertical') {
    return (
      <div className="space-y-2">
        {healthCategories.map((category) => (
          <Link
            key={category.id}
            href={category.href}
            className="flex items-start gap-4 p-4 rounded-lg bg-card border border-border transition-smooth hover:shadow-elevation-md hover:border-primary group"
            onMouseEnter={() => setHoveredCategory(category.id)}
            onMouseLeave={() => setHoveredCategory(null)}
          >
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 transition-smooth group-hover:bg-primary group-hover:scale-110">
              <Icon 
                name={category.icon as any} 
                size={24} 
                className={`transition-smooth ${
                  hoveredCategory === category.id 
                    ? 'text-primary-foreground' 
                    : 'text-primary'
                }`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold text-foreground mb-1 group-hover:text-primary transition-smooth">
                {category.label}
              </h3>
              {showDescriptions && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {category.description}
                </p>
              )}
            </div>
            <Icon 
              name="ChevronRightIcon" 
              size={20} 
              className="text-muted-foreground group-hover:text-primary transition-smooth flex-shrink-0 mt-1"
            />
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {healthCategories.map((category) => (
        <Link
          key={category.id}
          href={category.href}
          className="flex flex-col items-center text-center p-6 rounded-lg bg-card border border-border transition-smooth hover:shadow-elevation-lg hover:border-primary hover:-translate-y-1 group"
          onMouseEnter={() => setHoveredCategory(category.id)}
          onMouseLeave={() => setHoveredCategory(null)}
        >
          <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4 transition-smooth group-hover:bg-primary group-hover:scale-110">
            <Icon 
              name={category.icon as any} 
              size={32} 
              className={`transition-smooth ${
                hoveredCategory === category.id 
                  ? 'text-primary-foreground' 
                  : 'text-primary'
              }`}
            />
          </div>
          <h3 className="font-heading font-semibold text-foreground mb-2 group-hover:text-primary transition-smooth">
            {category.label}
          </h3>
          {showDescriptions && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {category.description}
            </p>
          )}
        </Link>
      ))}
    </div>
  );
};

export default HealthVerticalMenu;